'use client';

import {HeroMediaSectionFieldsFragment} from '@generated/graphql';
import {Box, Container, Typography} from '@mui/material';
import ContentfulImage from '@/components/ui/image/ContentfulImage';
import Video from '@/components/ui/video/Video';
import ConditionalLinkButton from '@/components/ui/buttons/ConditionalLinkButton';
import {isRenderableLinkButton} from '@/lib/ensure-link-button';
import HeroCtaCard from '@/components/sections/hero-media-section/HeroCtaCard';
import {
  StyledHeroV2CardsRow,
  StyledHeroV2CtaButton,
  StyledHeroV2Eyebrow,
  StyledHeroV2MediaBox,
  StyledHeroV2Section,
  StyledHeroV2TextColumn,
  StyledHeroV2TopRow,
} from '@/components/sections/hero-media-section/HeroMediaSectionV2.styles';

type HeroCardCollection = NonNullable<HeroMediaSectionFieldsFragment['ctaCardsCollection']>;
type HeroCardItem = NonNullable<HeroCardCollection['items'][number]>;
type HeroCardLink = NonNullable<HeroCardItem['cardCtaLink']>;

function cardLinkHasModalSections(link: HeroCardLink | null | undefined): boolean {
  return !!link?.modalSectionsCollection?.items.some(Boolean);
}

// A card is renderable when it has an icon image and a link that either
// navigates somewhere or opens a modal of sections (modal takes precedence).
function isRenderableHeroCard(card: HeroCardItem | null): card is HeroCardItem & {
  cardImage: {url: string};
  cardCtaLink: HeroCardLink;
} {
  if (!card) return false;
  const img = card.cardImage;
  const link = card.cardCtaLink;
  const hasImage = !!img?.url && img.url.trim() !== '';
  if (!hasImage || !link) return false;
  const hasLink = !!link.linkTo && link.linkTo.trim() !== '';
  return hasLink || cardLinkHasModalSections(link);
}

const HeroMediaSectionV2 = (fields: HeroMediaSectionFieldsFragment) => {
  const ctaButton = fields.ctaButton ?? null;
  const mediaUrl = fields.media?.url ?? '';
  const isVideo = Boolean(fields.media?.contentType?.startsWith('video/'));
  const showMedia = Boolean(mediaUrl);

  const cards = (fields.ctaCardsCollection?.items ?? []).filter(isRenderableHeroCard).map((card) => ({
    id: card.sys.id,
    heading: card.heading ?? '',
    iconUrl: card.cardImage.url,
    href: card.cardCtaLink.linkTo?.trim() ?? '',
    isExternal: !!card.cardCtaLink.isExternalLink,
    modalSections: card.cardCtaLink.modalSectionsCollection?.items ?? null,
    focalX: card.focalX,
    focalY: card.focalY,
  }));

  return (
    <StyledHeroV2Section data-hero-media id={fields.id ?? undefined}>
      <Container maxWidth='mdlg'>
        <StyledHeroV2TopRow>
          <StyledHeroV2TextColumn>
            {fields.header && <StyledHeroV2Eyebrow variant='overline'>{fields.header}</StyledHeroV2Eyebrow>}
            {fields.heading && (
              <Typography variant='h1' component='h1'>
                {fields.heading}
              </Typography>
            )}
            {fields.subHeading && (
              <Typography variant='h4' component='p' sx={{fontWeight: 400}}>
                {fields.subHeading}
              </Typography>
            )}
            {fields.subText && (
              <Typography variant='body1' sx={{opacity: 0.9}}>
                {fields.subText}
              </Typography>
            )}
            {isRenderableLinkButton(ctaButton) && (
              <Box sx={{marginTop: 2}}>
                <ConditionalLinkButton button={ctaButton} ButtonComponent={StyledHeroV2CtaButton} />
              </Box>
            )}
          </StyledHeroV2TextColumn>

          {showMedia && (
            <StyledHeroV2MediaBox>
              {isVideo ? (
                <Video
                  key={mediaUrl}
                  src={mediaUrl}
                  controls={false}
                  fillViewport={false}
                  posterImage={fields.posterImage?.url ?? ''}
                  usePosterImage={Boolean(fields.posterImage?.url)}
                  title={fields.altText ?? fields.heading ?? ''}
                />
              ) : (
                <ContentfulImage
                  src={mediaUrl}
                  alt={fields.altText ?? fields.heading ?? ''}
                  fill
                  sizes='(max-width: 1100px) 100vw, 50vw'
                  style={{objectFit: 'cover'}}
                />
              )}
            </StyledHeroV2MediaBox>
          )}
        </StyledHeroV2TopRow>

        {cards.length > 0 && (
          <StyledHeroV2CardsRow>
            {cards.map((card) => (
              <HeroCtaCard
                key={card.id}
                heading={card.heading}
                iconUrl={card.iconUrl}
                href={card.href}
                isExternal={card.isExternal}
                modalSections={card.modalSections}
                focalX={card.focalX}
                focalY={card.focalY}
                contentId={card.id}
              />
            ))}
          </StyledHeroV2CardsRow>
        )}
      </Container>
    </StyledHeroV2Section>
  );
};

export default HeroMediaSectionV2;
